import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react'
import PropTypes from 'prop-types'
import { CircularProgress, Grid, TextField } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'

import { SecaoGenerica, ModalConfirmacao } from 'components'
import { FileManager } from 'components/FileManager'
import { getLabelTipoVestigioEspecifico, getTipoVestigioLabel } from 'helpers/labels'
import { useFormEditarDescricaoTecnica } from './useFormEditarDescricaoTecnica'
import { FormDescricaoVestigioQuimico } from './components/FormDescricaoVestigioQuimico'
import { FormDescricaoVestigioBalistico } from './components/FormDescricaoVestigioBalistico'
import { useStyles } from './FormEditarDescricaoTecnica.styles'

const tiposVestigiosSemModelo = ['GERAL', 'BIOLOGICO', 'ELETRONICO', 'PAPILOSCOPICO', 'DOCUMENTAL', 'MERCEOLOGICO']

export const FormEditarDescricaoTecnica = forwardRef((props, ref) => {
  const {
    setOpenEditarDescricaoTecnica,
    idExameRealizado,
    idVestigio,
    fetchDescricoesTecnicasDeVestigio,
    setAtualizado,
    descricaoADuplicar,
    setDescricaoADuplicar,
    editarDescricao,
    setEditarDescricao,
    onStateChange,
  } = props

  const [arquivos, setArquivos] = useState([])

  /**
   * FileManager é um componente pesado, que faz busca pelos arquivos e exibe eles. Separá-lo 
   * do MainForm evita que ele seja renderizado toda vez que o formulário de descrição técnica
   * é renderizado.
   */
  return (
    <MainForm
      ref={ref}
      arquivos={arquivos}
      setOpenEditarDescricaoTecnica={setOpenEditarDescricaoTecnica}
      idExameRealizado={idExameRealizado}
      idVestigio={idVestigio}
      fetchDescricoesTecnicasDeVestigio={fetchDescricoesTecnicasDeVestigio}
      descricaoADuplicar={descricaoADuplicar}
      setDescricaoADuplicar={setDescricaoADuplicar}
      setAtualizado={setAtualizado || null}
      editarDescricao={editarDescricao}
      setEditarDescricao={setEditarDescricao}
      onStateChange={onStateChange}
    >
      <FileManager
        rotaUpload="/arquivo/descricaoTecnica/examinador"
        rotaLoadImagem="/arquivo/descricaoTecnica/examinador"
        setArquivosPai={setArquivos}
        arquivosIniciais={[]}
        tipos={['image']}
      />
    </MainForm>
  )
})

FormEditarDescricaoTecnica.propTypes = {
  setOpenEditarDescricaoTecnica: PropTypes.func.isRequired,
  idExameRealizado: PropTypes.number.isRequired,
  idVestigio: PropTypes.number.isRequired,
  fetchDescricoesTecnicasDeVestigio: PropTypes.func.isRequired,
  setAtualizado: PropTypes.func,
  descricaoADuplicar: PropTypes.object,
  setDescricaoADuplicar: PropTypes.func.isRequired,
  editarDescricao: PropTypes.bool.isRequired,
  setEditarDescricao: PropTypes.func.isRequired,
  onStateChange: PropTypes.func,
}

export const MainForm = forwardRef((props, ref) => {
  const {
    idVestigio,
    idExameRealizado,
    setOpenEditarDescricaoTecnica,
    fetchDescricoesTecnicasDeVestigio,
    arquivos,
    setAtualizado,
    children,
    descricaoADuplicar,
    setDescricaoADuplicar,
    editarDescricao,
    setEditarDescricao,
    onStateChange,
  } = props

  const classes = useStyles()

  const {
    tiposVestigios,
    tiposVestigiosEspecificos,
    processandoTiposVestigios,
    processandoOperacao,
    isFormularioInvalido,
    exameRealizado,
    formDescricaoTecnica,
    handleClickCancelarOperacao,
    handleChangeTipoVestigio,
    handleConfirmarDescricaoTecnica,
  } = useFormEditarDescricaoTecnica({
    idVestigio,
    idExameRealizado,
    setOpenEditarDescricaoTecnica,
    fetchDescricoesTecnicasDeVestigio,
    arquivos,
    setAtualizado,
    descricaoADuplicar,
    setDescricaoADuplicar,
    editarDescricao,
    setEditarDescricao,
  })

  useImperativeHandle(ref, () => ({
    submit: handleConfirmarDescricaoTecnica,
    cancel: handleClickCancelarOperacao,
  }), [handleConfirmarDescricaoTecnica, handleClickCancelarOperacao])

  useEffect(() => {
    if (onStateChange) {
      onStateChange(!isFormularioInvalido, processandoOperacao)
    }
  }, [isFormularioInvalido, processandoOperacao, onStateChange])

  return (
    <div>
      {processandoTiposVestigios ? (
        <div className={classes.carregando}>
          <CircularProgress disableShrink />
        </div>
      ) : (
        <Grid container>
          <Grid item xs={12} className={classes.formContent}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  value={formDescricaoTecnica.formState.values.tipoVestigioGenerico || null}
                  name="tipoVestigioGenerico"
                  noOptionsText="Sem informações"
                  options={tiposVestigios.map((tipo) => tipo.identificador) || []}
                  onChange={(event, newValue) => {
                    handleChangeTipoVestigio(newValue)
                  }}
                  getOptionLabel={(option) => getTipoVestigioLabel(option)?.toUpperCase() || ''}
                  renderOption={(option) => getTipoVestigioLabel(option)?.toUpperCase() || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={formDescricaoTecnica.formState.values.tipoVestigioGenerico || 'Selecionar...'}
                      label="NATUREZA DO VESTÍGIO (GENÉRICA)"
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                      error={formDescricaoTecnica.hasError('tipoVestigioGenerico')}
                      helperText={formDescricaoTecnica.hasError('tipoVestigioGenerico') ? formDescricaoTecnica.formState.errors.tipoVestigioGenerico[0] : null}
                    />
                  )}
                  disabled={editarDescricao}
                  className={classes.tipoVestigio}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  value={formDescricaoTecnica.formState.values.tipoVestigioEspecifico || null}
                  name="tipoVestigioEspecifico"
                  noOptionsText="Sem informações"
                  options={tiposVestigiosEspecificos.map((tipo) => tipo.identificador) || []}
                  onChange={(event, newValue) => {
                    formDescricaoTecnica.handleChangeNewValueByNameProperty(event, newValue, 'tipoVestigioEspecifico')
                  }}
                  getOptionLabel={(option) => getLabelTipoVestigioEspecifico(option)?.toUpperCase() || ''}
                  renderOption={(option) => getLabelTipoVestigioEspecifico(option)?.toUpperCase() || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={formDescricaoTecnica.formState.values.tipoVestigioEspecifico || 'Selecionar...'}
                      label="NATUREZA DO VESTÍGIO (ESPECÍFICA)"
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                      error={formDescricaoTecnica.hasError('tipoVestigioEspecifico')}
                      helperText={formDescricaoTecnica.hasError('tipoVestigioEspecifico') ? formDescricaoTecnica.formState.errors.tipoVestigioEspecifico[0] : null}
                    />
                  )}
                  className={classes.tipoVestigio}
                />
              </Grid>

              {formDescricaoTecnica.formState.values.tipoVestigioEspecifico &&
                (formDescricaoTecnica.formState.values.tipoVestigioEspecifico === 'OUTRO' ||
                  formDescricaoTecnica.formState.values.tipoVestigioEspecifico === 'ARTEFATO' ||
                  tiposVestigiosSemModelo.includes(formDescricaoTecnica.formState.values.tipoVestigioGenerico)) && (
                  <Grid item xs={12}>
                    <TextField
                      variant="outlined"
                      label="DESCRIÇÃO"
                      required
                      fullWidth
                      value={formDescricaoTecnica.formState.values.descricao || ''}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      name="descricao"
                      onChange={formDescricaoTecnica.handleChange}
                      error={formDescricaoTecnica.hasError('descricao')}
                      helperText={formDescricaoTecnica.hasError('descricao') ? formDescricaoTecnica.formState.errors.descricao[0] : null}
                    />
                  </Grid>
                )}
            </Grid>
          </Grid>

          {formDescricaoTecnica.formState.values.tipoVestigioGenerico === 'QUIMICO' &&
            formDescricaoTecnica.formState.values.tipoVestigioEspecifico && (
              <FormDescricaoVestigioQuimico formDescricaoTecnica={formDescricaoTecnica} />
            )}

          {formDescricaoTecnica.formState.values.tipoVestigioGenerico === 'BALISTICO' &&
            formDescricaoTecnica.formState.values.tipoVestigioEspecifico && (
              <FormDescricaoVestigioBalistico
                descricaoDuplicada={!!descricaoADuplicar?.id}
                formDescricaoTecnica={formDescricaoTecnica}
                tipoExame={exameRealizado.tipo}
              />
            )}

          <SecaoGenerica titulo="IMAGENS RELACIONADAS">
            <Grid item container xs={12} spacing={2}>
              <Grid item xs={12}>
                {children}
              </Grid>
            </Grid>
          </SecaoGenerica>
        </Grid>
      )}
    </div>
  )
})

MainForm.propTypes = {
  idVestigio: PropTypes.number.isRequired,
  idExameRealizado: PropTypes.number.isRequired,
  setOpenEditarDescricaoTecnica: PropTypes.func.isRequired,
  fetchDescricoesTecnicasDeVestigio: PropTypes.func.isRequired,
  arquivos: PropTypes.array.isRequired,
  setAtualizado: PropTypes.func,
  children: PropTypes.node,
  descricaoADuplicar: PropTypes.object,
  setDescricaoADuplicar: PropTypes.func.isRequired,
  editarDescricao: PropTypes.bool.isRequired,
  setEditarDescricao: PropTypes.func.isRequired,
  onStateChange: PropTypes.func,
}

/** 
  Foi necessário criar esse subcomponente para evitar rerenderizações ao mudar os estados do formulário
  de adicionar descrição técnica. Fazer isso, apenas mantém os estados de controle do formulário separados
  do componente pai, evitando assim problemas de desempenho.
**/
export const FormEditarDescricaoTecnicaModal = forwardRef((props, ref) => {
  const {
    idVestigio,
    idExameRealizado,
    fetchDescricoesTecnicasDeVestigio,
    setAtualizado,
  } = props

  const [open, setOpen] = useState(false)
  const [editarDescricao, setEditarDescricao] = useState(false)
  const [descricaoADuplicar, setDescricaoADuplicar] = useState(null)

  const [formValid, setFormValid] = useState(false)
  const [formProcessing, setFormProcessing] = useState(false)
  const formRef = useRef(null)

  useImperativeHandle(ref, () => ({
    abrirCadastro: () => {
      setEditarDescricao(false)
      setDescricaoADuplicar(null)
      setOpen(true)
    },
    abrirEdicao: (descricao) => {
      setEditarDescricao(true)
      setDescricaoADuplicar(descricao)
      setOpen(true)
    },
    abrirDuplicacao: (descricao) => {
      setEditarDescricao(false)
      setDescricaoADuplicar(descricao)
      setOpen(true)
    }
  }))

  const handleFormValidityChange = (isValid, isProcessing) => {
    setFormValid(isValid)
    setFormProcessing(isProcessing)
  }

  return (
    <ModalConfirmacao
      aberto={open}
      onClose={() => {
        if (formRef.current && formRef.current.cancel) {
          formRef.current.cancel()
        } else {
          setOpen(false)
        }
      }}
      onConfirm={() => {
        if (formRef.current && formRef.current.submit) {
          formRef.current.submit()
        }
      }}
      disabled={!formValid}
      processando={formProcessing}
      titulo={editarDescricao ? "EDITAR DESCRIÇÃO TÉCNICA" : "ADICIONAR DESCRIÇÃO TÉCNICA"}
      confirmarLabel="Confirmar"
      cancelarLabel="Cancelar"
      confirmarColor="success"
      maxWidth="md"
    >
      {open && (
        <FormEditarDescricaoTecnica
          ref={formRef}
          idVestigio={idVestigio}
          idExameRealizado={idExameRealizado}
          setOpenEditarDescricaoTecnica={setOpen}
          fetchDescricoesTecnicasDeVestigio={fetchDescricoesTecnicasDeVestigio}
          descricaoADuplicar={descricaoADuplicar}
          setDescricaoADuplicar={setDescricaoADuplicar}
          setAtualizado={setAtualizado || null}
          editarDescricao={editarDescricao}
          setEditarDescricao={setEditarDescricao}
          onStateChange={handleFormValidityChange}
        />
      )}
    </ModalConfirmacao>
  )
})

FormEditarDescricaoTecnicaModal.propTypes = {
  idVestigio: PropTypes.number.isRequired,
  idExameRealizado: PropTypes.number.isRequired,
  fetchDescricoesTecnicasDeVestigio: PropTypes.func.isRequired,
  setAtualizado: PropTypes.func,
}
